import { all, call, fork, put, take } from 'redux-saga/effects';

import { message } from 'antd';
import { updateNoteToCard } from 'containers/MainLayout/actions';
import translations from 'translations';
import { ProductSortField, QueryProductSearchArgs, SortDirection } from 'types/schema';
import {
  addFavorite,
  checkValidEmail,
  contactUs,
  getProducts,
  handleWriteContactUsInfo,
  removeFavorite,
  setProductNote,
} from 'utils/apollo';
import utilMessages from 'utils/messages';
import {
  checkEmail as checkEmailActions,
  contactUs as contactUsActions,
  productSourceFavorite,
  productSourceSetNote,
  productsSource,
} from './actions';
import ActionTypes from './constants';
import messages from './messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';

interface ISectionParams {
  tag: string;
}

export const isMobile = window.document.body.clientWidth < 768;

export const tagsList = ['LETTUCE_FARM', 'VEGETABLE_FARM', 'HERBS_FARM', 'MUSHROOM_FARM'];

export const CONTACT_ID_FROM_LANDING_PAGE = 'contactIdFromLandingPage';

function* getSectionData(section: ISectionParams) {
  const params: QueryProductSearchArgs = {
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: { tags: [section.tag] },
    pagination: { page: 0, size: 1000 },
  };
  const response = yield call(getProducts, params);

  if (response.errors) {
    yield put(productsSource.failure({ [section.tag]: { data: [], error: true } }));
  } else {
    yield put(
      productsSource.success({
        [section.tag]: {
          data: response.data,
          loading: false,
          error: false,
          totalResults: response.totalResults,
        },
      }),
    );
  }
}

function* loadAllSectionData() {
  const initData = tagsList.reduce((acc, item) => {
    return {
      ...acc,
      [item]: {
        loading: true,
        data: [],
      },
    };
  }, {});
  yield put(productsSource.request(initData));
  yield all(
    tagsList.map((tag) => {
      return call(getSectionData, { tag: tag });
    }),
  );
}

function* productSourceFavoriteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.PRODUCTS_SOURCE_FAVORITE);
    yield put(productSourceFavorite.request());
    const {
      product: { isFavorite, id },
      index,
      section,
    } = payload;

    const response = yield call(isFavorite ? removeFavorite : addFavorite, { id: id });
    if (response.errors) {
      yield put(productSourceFavorite.failure({ index: index, section: section }));
    } else {
      yield put(productSourceFavorite.success({ index: index, section: section, isFavorite: !isFavorite }));
    }
  }
}

function* productSourceSetNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_PRODUCT_NOTE);
    yield put(productSourceSetNote.request());

    const { id, note, index, section } = payload;
    const response = yield call(setProductNote, { input: { productId: id, note: note } });
    if (response.errors) {
      yield put(productSourceSetNote.failure(response.errors));
      message.error(translations(utilMessages[payload.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    } else {
      yield put(productSourceSetNote.success({ note: note, index: index, section: section }));
      yield put(updateNoteToCard(response.product));
      message.success(translations(utilMessages[payload.note ? 'addItemNoteSuccess' : 'deleteItemNoteSuccess']));
    }
  }
}

function* contactUsFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.CONTACT_US_SUBMIT);
    const { values, cb } = payload;
    values.lang = localStorage.getItem('lang') || 'vi';
    yield put(contactUsActions.request());
    const response = yield call(contactUs, {
      input: {
        ...values,
        contactId: window.localStorage.getItem(CONTACT_ID_FROM_LANDING_PAGE) || '',
      },
    });
    if (!response.errors) {
      yield put(contactUsActions.success());
      if (cb) {
        cb();
        if (window.localStorage.getItem(CONTACT_ID_FROM_LANDING_PAGE)) {
          window.localStorage.removeItem(CONTACT_ID_FROM_LANDING_PAGE);
        }
        message.success(translations(messages.contactUsSuccess));
        sendGA4Event(EventName.START_SOURCING_FORM_SUBMIT_FTB, {
          start_sourcing_form_submit_status: 'SUCCESS',
        });
      }
    } else {
      yield put(contactUsActions.failure(response.errors));
      response.errors.map((e: any) => message.error(e.message));
      sendGA4Event(EventName.START_SOURCING_FORM_SUBMIT_FTB, {
        start_sourcing_form_submit_status: 'ERROR',
      });
    }
  }
}

function* checkEmailFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.CHECK_EMAIL_SUBMIT);
    yield put(checkEmailActions.request());
    const response = yield call(checkValidEmail, { email: payload });
    if (!response.errors) {
      yield put(checkEmailActions.success(response));
    } else {
      yield put(checkEmailActions.failure(response.errors[0].message));
    }
  }
}

function* submitContactShortForm() {
  while (true) {
    const { payload } = yield take(ActionTypes.CONTACT_US_SHORT_FORM_REQUEST);
    const { data, cb } = payload;
    const response = yield call(handleWriteContactUsInfo, {
      input: {
        contactUsInfos: [data],
      },
    });
    if (!response.errors && cb) {
      cb();
      window.localStorage.setItem(CONTACT_ID_FROM_LANDING_PAGE, response?.contactUsInfoList?.[0]?.contactId || '');
      sendGA4Event(EventName.GET_PRODUCT_CONSULTATION_FORM_SUBMIT_FTB, {
        get_product_consultation_form_submit_status: 'SUCCESS',
      });
    } else {
      message.error('Something went wrong');
      sendGA4Event(EventName.GET_PRODUCT_CONSULTATION_FORM_SUBMIT_FTB, {
        get_product_consultation_form_submit_status: 'ERROR',
      });
    }
  }
}

export default function* saga() {
  yield call(loadAllSectionData);
  yield fork(productSourceFavoriteFlow);
  yield fork(productSourceSetNoteFlow);
  yield fork(contactUsFlow);
  yield fork(checkEmailFlow);
  yield fork(submitContactShortForm);
}
